import { useState, useEffect } from 'react';
import { Product } from '@/mocks/products';

interface ProductQuickViewProps {
  product: Product | null;
  onClose: () => void;
}

export default function ProductQuickView({ product, onClose }: ProductQuickViewProps) {
  const [activeImage, setActiveImage] = useState<string>('');

  useEffect(() => {
    if (product) {
      setActiveImage(product.image);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [product]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  if (!product) return null;

  const galleryImages = product.images && product.images.length > 1 ? product.images : null;
  const displayImage = activeImage || product.image;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

      {/* Modal */}
      <div
        className="relative bg-white rounded-2xl overflow-hidden w-full max-w-3xl max-h-[90vh] overflow-y-auto animate-fade-in"
        onClick={(e) => e.stopPropagation()}
        style={{ animation: 'quickViewIn 0.3s ease-out' }}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-9 h-9 bg-white/90 rounded-full flex items-center justify-center text-gray-500 hover:text-gray-800 hover:bg-white transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-xl"></i>
        </button>

        <div className="flex flex-col md:flex-row">
          {/* Image Side */}
          <div className="md:w-1/2 relative bg-gradient-to-br from-stone-50 to-white flex flex-col">
            <div className="aspect-square w-full overflow-hidden">
              <img
                src={displayImage}
                alt={product.name}
                className="w-full h-full object-cover transition-all duration-300"
              />
            </div>
            {product.badge && (
              <span className="absolute top-4 left-4 bg-amber-500 text-white text-xs font-semibold px-3 py-1.5 rounded-full">
                {product.badge}
              </span>
            )}

            {/* Thumbnails */}
            {galleryImages && (
              <div className="flex gap-2 p-3 overflow-x-auto" style={{ background: '#faf7f2' }}>
                {galleryImages.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveImage(img)}
                    className="flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden transition-all cursor-pointer"
                    style={{
                      border: displayImage === img ? '2px solid #c9a96e' : '2px solid #e8d5b7',
                      opacity: displayImage === img ? 1 : 0.7,
                    }}
                  >
                    <img src={img} alt={`View ${idx + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Content Side */}
          <div className="md:w-1/2 p-6 md:p-8 flex flex-col justify-between">
            <div>
              <span className="text-amber-600 text-xs font-bold uppercase tracking-widest">
                {product.brand}
              </span>
              <h2 className="font-display text-2xl md:text-3xl font-bold text-stone-900 mt-1 mb-2">
                {product.name}
              </h2>
              <p className="text-gray-500 text-sm mb-4 leading-relaxed">
                {product.details || product.description}
              </p>

              {/* Tags */}
              {product.tags && product.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-5">
                  {product.tags.map((tag) => (
                    <span
                      key={tag}
                      className="bg-amber-50 text-amber-700 text-xs font-medium px-3 py-1 rounded-full border border-amber-100"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              {/* Weight */}
              {product.weight && (
                <div className="flex items-center gap-2 mb-5 text-sm text-gray-500">
                  <i className="ri-scales-line text-amber-500"></i>
                  <span>Net Weight: <strong className="text-stone-800">{product.weight}</strong></span>
                </div>
              )}


            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-3">
              <a
                href="https://wa.me/918800889959"
                target="_blank"
                rel="nofollow noreferrer"
                className="w-full font-semibold py-3 rounded-xl transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
                style={{ background: '#3b2010', color: '#faf7f2' }}
              >
                <i className="ri-whatsapp-line"></i>
                Enquire on WhatsApp
              </a>
              <a
                href="/contact"
                className="w-full font-semibold py-3 rounded-xl transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
                style={{ border: '1px solid #c9a96e', color: '#3b2010', background: 'transparent' }}
              >
                <i className="ri-mail-send-line"></i>
                Contact to Order
              </a>
            </div>

            {/* Trust Badges */}
            <div className="flex items-center gap-4 mt-5 pt-5 border-t border-stone-100">
              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                <i className="ri-shield-check-line text-green-700"></i>
                <span>FSSAI Certified</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                <i className="ri-truck-line text-green-700"></i>
                <span>Pan-India Delivery</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                <i className="ri-refresh-line text-green-700"></i>
                <span>7-day returns</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes quickViewIn {
          from { opacity: 0; transform: scale(0.95) translateY(10px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
}
